import { Suspense, lazy, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { getPdfUrl } from '@/features/story/api';
import { sanitizeFilename } from '@/lib/pdf-filename';
import { STRINGS } from '@/strings/strings';

const PdfDocumentViewer = lazy(() => import('./PdfDocumentViewer'));

interface LocationState {
  url?: string;
  storyTitle?: string;
}

/**
 * Full-screen in-app PDF viewer for the Homescreen-PWA branch of
 * PdfPlaceholderButton (PWA #16 Sub-Phase 16.6). Browser-mode never
 * visits this route — Mobile Safari + Desktop open the signed PDF
 * inline in a new tab via native QuickLook / browser viewer.
 *
 * Layout: fixed-position container filling the viewport with two
 * overlay buttons (back top-left, download top-right) and the
 * lazy-loaded react-pdf document viewer in between. AppShell
 * suppresses TopBar + BottomNav + EmailVerificationBanner +
 * MiniPlayer on this route — see the `onPdfViewerPage` regex in
 * `AppShell.tsx`.
 *
 * URL acquisition strategy:
 *   1. Read from `location.state.url` (passed by PdfPlaceholderButton
 *      on the click that navigated here — single-fetch happy path).
 *   2. If state is missing (deep-link, page refresh, browser back/
 *      forward losing state), refetch via `getPdfUrl(storyId)`. No
 *      409-retry here: by the time the user reached this route,
 *      pdfStatus had to be 'ready' on the source page, and the
 *      on-button retry already resolved any signing-in-flight race.
 *
 * Download-button: tries Web Share API with a File payload
 * (`navigator.share({ files: [pdfFile] })`), falls back to a
 * programmatic anchor with `download` attribute. iOS classifies
 * `share({ url })` as a URL-share and offers AirDrop / Mail / app
 * targets but NOT "Save to Files" (parents can't save locally —
 * surfaced by Sebastian 2026-04-30). `share({ files })` is a
 * file-share and surfaces the full file-action set including
 * "Save to Files" and "Save to Photos". To produce the File we
 * fetch the signed S3 URL → Blob → File; this requires the bucket
 * to expose `Access-Control-Allow-Origin` for the PWA origin. If
 * the fetch or share fails we fall through to the anchor download.
 * The button shows a spinner while the fetch is in flight (~1-2s
 * on 5G for ~500 KB PDFs).
 *
 * Back-button: routes via `navigate(-1)` when state.url is present
 * (came via PdfPlaceholderButton → history exists). For deep-link or
 * refresh cases (no state), falls back to `/library` so the user
 * never lands on a dead back-stack.
 */
export function PdfViewerRoute() {
  const { storyId } = useParams<{ storyId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const stateUrl = (location.state as LocationState | null)?.url ?? null;
  const stateTitle = (location.state as LocationState | null)?.storyTitle ?? undefined;

  const [url, setUrl] = useState<string | null>(stateUrl);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const copy = STRINGS.storyOutput.page.pdf;

  useEffect(() => {
    if (url || !storyId) return;
    let cancelled = false;
    getPdfUrl(storyId)
      .then((res) => {
        if (!cancelled) setUrl(res.url);
      })
      .catch(() => {
        if (!cancelled) setError(copy.errors.loadFailed);
      });
    return () => {
      cancelled = true;
    };
  }, [url, storyId, copy.errors.loadFailed]);

  const handleBack = () => {
    if (stateUrl) {
      navigate(-1);
    } else {
      navigate('/library');
    }
  };

  const handleDownload = async () => {
    if (!url || isDownloading) return;
    const filename = `${sanitizeFilename(stateTitle)}.pdf`;

    // File-share path — preferred on iOS Homescreen-PWA. iOS routes
    // share({ url }) to the URL-share action set (AirDrop / Mail /
    // app targets) which omits "Save to Files"; share({ files })
    // routes to the file-share action set with "Save to Files",
    // "Save to Photos", and the relevant document app targets.
    if (navigator.share && navigator.canShare) {
      try {
        setIsDownloading(true);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
        const blob = await response.blob();
        const file = new File([blob], filename, { type: 'application/pdf' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: stateTitle ?? 'Dreamable Story',
          });
          setIsDownloading(false);
          return;
        }
        // canShare({ files }) returned false — platform supports
        // Web Share but not the File payload (older iOS, some
        // Android). Fall through to the anchor.
        setIsDownloading(false);
      } catch (err) {
        setIsDownloading(false);
        // User-cancelled share-sheet → no-op, do not fall back to the
        // anchor (we'd open a second download UI immediately after the
        // user explicitly dismissed the first).
        if ((err as Error).name === 'AbortError') return;
        // Fetch / share errors (CORS, network, share-sheet failure)
        // → fall through to the anchor.
      }
    }

    // Anchor fallback for Desktop and any platform without Web Share
    // API or file-payload support. Browsers that don't honour the
    // download attribute will still see the PDF (open inline / new
    // tab) which is at least visible to the user.
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener,noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="fixed inset-0 z-40 bg-dream-navy" data-testid="pdf-viewer-root">
      <button
        type="button"
        onClick={handleBack}
        aria-label={copy.viewer.backLabel}
        data-testid="pdf-viewer-back"
        className="fixed left-4 top-[max(env(safe-area-inset-top,0),1rem)] z-50 flex h-11 w-11 items-center justify-center rounded-full bg-dream-navy/80 text-dream-white shadow-lg backdrop-blur transition hover:bg-dream-navy"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>

      {url && !error && (
        <button
          type="button"
          onClick={handleDownload}
          disabled={isDownloading}
          aria-label={copy.viewer.downloadLabel}
          aria-busy={isDownloading}
          data-testid="pdf-viewer-download"
          className="fixed right-4 top-[max(env(safe-area-inset-top,0),1rem)] z-50 flex h-11 w-11 items-center justify-center rounded-full bg-dream-navy/80 text-dream-white shadow-lg backdrop-blur transition hover:bg-dream-navy disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isDownloading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Download className="h-5 w-5" />
          )}
        </button>
      )}

      {error ? (
        <div className="flex h-full w-full items-center justify-center px-6 text-center text-dream-white">
          <p className="text-sm">{error}</p>
        </div>
      ) : url ? (
        <Suspense
          fallback={
            <div
              className="flex h-full w-full items-center justify-center text-dream-white"
              aria-live="polite"
            >
              <Loader2 className="h-8 w-8 animate-spin" aria-label={copy.viewer.loading} />
            </div>
          }
        >
          <PdfDocumentViewer url={url} onError={() => setError(copy.errors.loadFailed)} />
        </Suspense>
      ) : (
        <div
          className="flex h-full w-full items-center justify-center text-dream-white"
          aria-live="polite"
        >
          <Loader2 className="h-8 w-8 animate-spin" aria-label={copy.viewer.loading} />
        </div>
      )}
    </div>
  );
}
